import { useLayoutEffect, useRef, useState } from 'react';

/**
 * Custom hook that detects if the chips within a container overflow its visible area.
 * It uses ResizeObserver and MutationObserver to monitor size changes and DOM mutations.
 *
 * @param containerRef - A ref object pointing to the container element of the chips.
 * @param deps - An optional array of dependencies that will trigger re-evaluation when changed.
 *
 * @returns A boolean indicating whether the chips overflow the container.
 */
export function useChipsOverflow(
  containerRef: React.RefObject<HTMLDivElement>,
  deps: unknown[] = [],
) {
  const [overflow, setOverflow] = useState(false);
  const roRef = useRef<ResizeObserver | null>(null);
  const moRef = useRef<MutationObserver | null>(null);
  const rafIdRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const measure = () => {
      rafIdRef.current = null;
      // only measure real chips
      const chips = Array.from(el.querySelectorAll<HTMLElement>('[data-chip]'));
      if (chips.length <= 1) {
        setOverflow((prev) => {
          if (prev !== false) return false;
          return prev;
        });
        return;
      }
      const first = chips[0];
      const last = chips[chips.length - 1];

      // wrapped = moved to a new row
      const wrapped = last.offsetTop > first.offsetTop;

      // also fall back to actual overflow for single-line layouts
      const clipped = el.scrollWidth - el.clientWidth > 1; // 1px tolerance

      const hasOverflow = wrapped || clipped;

      setOverflow((prev) => {
        if (prev !== hasOverflow) return hasOverflow;
        return prev;
      });
    };

    // Allows batching multiple measurements in a single frame
    // and computes animation frames correctly so we get up-to-date sizes
    const rafMeasure = () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      rafIdRef.current = requestAnimationFrame(measure);
    };

    // initial
    rafMeasure();

    // resize (container and chips)
    roRef.current = new ResizeObserver(rafMeasure);
    roRef.current.observe(el);

    // DOM mutations (chips added/removed) - don't observe attributes to avoid infinite loops
    moRef.current = new MutationObserver(rafMeasure);
    moRef.current.observe(el, { childList: true, subtree: true });

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      roRef.current?.disconnect();
      moRef.current?.disconnect();
      roRef.current = null;
      moRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, ...deps]);

  return overflow;
}
